import { motion } from "@/lib/motion-lite";
import { ShieldCheck, Languages, PaintRoller, BadgeCheck, Phone, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { siteConfig } from "@/lib/site-config";
import FormEmbed from "@/components/FormEmbed";

const badges = [
  { icon: ShieldCheck, label: "Licensed Colorado LLC" },
  { icon: BadgeCheck, label: "Free Written Estimates" },
  { icon: Languages, label: "English & Español" },
  { icon: PaintRoller, label: "Residential & Commercial" },
];

const stats = [
  { value: "100", suffix: "%", label: "Customer Satisfaction" },
  { value: "17", suffix: "+", label: "Cities Served" },
  { value: "15", suffix: "%", label: "New Client Discount" },
];

const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="home" className="relative min-h-[100svh] flex items-center overflow-hidden">
      <img
        src="/assets/images/hero-painting.webp"
        alt="Diaz Brothers Construction crew spray-painting a residential interior in Colorado"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        // React 18 doesn't map the camelCase prop; pass the DOM attribute directly
        {...{ fetchpriority: "high" }}
        decoding="async"
        width={1920}
        height={1280}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(210,40%,5%/0.85)] via-[hsl(210,40%,6%/0.7)] to-[hsl(202,70%,14%/0.5)]" />
      <div className="absolute inset-0 gradient-mesh opacity-40" />

      <div
        className="hero-blob-1 absolute top-20 right-20 w-96 h-96 rounded-full hidden md:block"
        style={{ background: "radial-gradient(circle, hsl(var(--secondary) / 0.3), transparent 70%)" }}
        aria-hidden="true"
      />
      <div
        className="hero-blob-2 absolute bottom-40 left-10 w-64 h-64 rounded-full hidden md:block"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.45), transparent 70%)" }}
        aria-hidden="true"
      />

      <div className="relative z-10 container-max px-4 pt-20 pb-32 md:pt-24 md:pb-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2 rounded-full mb-6 shadow-lg"
              >
                <PaintRoller className="h-4 w-4 text-secondary" />
                <span>Highlands Ranch, CO · Denver Metro &amp; Northern Colorado</span>
              </motion.div>

              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] mb-5 tracking-tight font-bold drop-shadow-[0_4px_24px_rgba(0,0,0,0.5)]">
                Painting, Remodeling
                <br />&amp; <span className="text-gradient-accent">Quality You Can Trust</span>
              </h1>

              <div className="inline-block bg-[#070d12]/60 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-4 mb-6 max-w-xl shadow-xl">
                <p className="text-white text-base md:text-lg leading-relaxed">
                  <span className="font-bold text-white">Diaz Brothers Construction LLC</span> handles
                  interior &amp; exterior painting, facade waterproofing, remodeling, drywall, and tile
                  installation for homes and businesses across Colorado — done right the first time.
                </p>
              </div>

              {/* Satisfaction stat + trust badges, directly under the subheading */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
                className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-8"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-4xl md:text-5xl font-bold text-secondary drop-shadow">
                    100<span className="text-3xl md:text-4xl">%</span>
                  </span>
                  <span className="text-white/90 text-sm font-semibold leading-tight max-w-[9rem]">
                    Customer Satisfaction
                  </span>
                </div>
                <div className="h-10 w-px bg-white/20 hidden sm:block" />
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {badges.map(({ icon: Icon, label }) => (
                    <span key={label} className="flex items-center gap-2 text-white/90 text-xs sm:text-sm font-medium">
                      <Icon className="h-4 w-4 text-secondary shrink-0" />
                      {label}
                    </span>
                  ))}
                </div>
              </motion.div>

              <div className="flex flex-wrap gap-3 mb-8">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-7 py-6 rounded-xl font-semibold shadow-xl"
                >
                  Get Free Estimate
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="text-base gap-2 px-7 py-6 rounded-xl border-2 border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm font-semibold"
                >
                  <a href={siteConfig.phoneHref}>
                    <Phone className="h-5 w-5" /> {siteConfig.phone}
                  </a>
                </Button>
                <Button
                  asChild
                  variant="ghost"
                  size="lg"
                  className="text-base gap-2 px-6 py-6 rounded-xl text-white hover:bg-white/10 font-semibold hidden sm:inline-flex"
                >
                  <a href={siteConfig.smsHref}>
                    <MessageSquare className="h-5 w-5" /> Text Us
                  </a>
                </Button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3"
              >
                <span className="text-2xl" aria-hidden="true">
                  🎁
                </span>
                <span className="text-white font-semibold text-sm">
                  <span className="text-secondary font-bold">15% off</span> your first project ·{" "}
                  <span className="text-secondary font-bold">10%</span> when you refer a friend
                </span>
              </motion.div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="widget-shell bg-card">
              <div className="px-6 pt-6 pb-2">
                <h2 className="font-heading text-2xl text-foreground font-bold tracking-tight">
                  Get Your Free Estimate
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Written, itemized, and no obligation. Se habla español.
                </p>
              </div>
              <FormEmbed className="p-4 sm:p-6" />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-10 lg:mt-12 glass-card rounded-2xl p-5 md:p-6 gradient-border"
        >
          <div className="flex flex-wrap items-center justify-around gap-6">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                className="text-center"
              >
                <div className="font-heading text-3xl md:text-4xl text-white tracking-wide font-bold">
                  {stat.value}
                  <span className="text-secondary">{stat.suffix}</span>
                </div>
                <div className="text-white/90 text-xs font-medium uppercase tracking-wider mt-1">
                  {stat.label}
                </div>
              </motion.div>
            ))}
            <div className="text-center">
              <div className="font-heading text-3xl md:text-4xl text-white tracking-wide font-bold">
                Mon<span className="text-secondary">–</span>Fri
              </div>
              <div className="text-white/90 text-xs font-medium uppercase tracking-wider mt-1">
                9 AM – 5 PM · Sat by Appt
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-20 md:h-24 leading-none">
        <svg
          viewBox="0 0 1440 96"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="block w-full h-full"
          preserveAspectRatio="none"
        >
          <path
            d="M0 0C240 96 480 96 720 64C960 32 1200 32 1440 64V96H0V0Z"
            fill="hsl(var(--background))"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
